import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LinkOutlined as LinkIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  VideocamOutlined as VideocamIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { MeetingCardProps } from "../../types/MeetingCard.types";

const MeetingCard: React.FC<MeetingCardProps> = ({
  calendarEvent,
  isLoading,
  error,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Laddar mötesinfo...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error || !calendarEvent) return null;

  const isOnlineMeeting =
    calendarEvent.isOnlineMeeting && calendarEvent.onlineMeeting?.joinUrl;
  const startTime = new Date(calendarEvent.localStartTime);
  const endTime = new Date(calendarEvent.localEndTime);
  const now = new Date();
  const isOngoing = startTime <= now && endTime > now;
  const isPast = endTime < now;
  const attendees = calendarEvent.attendees || [];

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const getAvatarColor = (name: string) => {
    const colors = ["#1976d2", "#388e3c", "#f57c00", "#d32f2f", "#7b1fa2", "#00796b", "#5d4037", "#455a64"];
    const hash = name.split("").reduce((a, b) => { a = (a << 5) - a + b.charCodeAt(0); return a & a; }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const statusColor = isOngoing ? "#2e7d32" : isPast ? "#757575" : "#1976d2";

  const handleJoinMeeting = () => {
    const teamsUrl = calendarEvent.onlineMeeting!.joinUrl;
    if (teamsUrl.includes("teams.microsoft.com")) {
      window.location.href = teamsUrl.replace("https://teams.microsoft.com", "msteams:");
    } else {
      window.location.href = teamsUrl;
    }
  };

  const handleOpenOutlook = () => {
    const outlookWebUrl =
      calendarEvent.webLink ||
      `https://outlook.office365.com/calendar/item/${encodeURIComponent(calendarEvent.id)}`;
    const outlookDesktopUrl = `outlook://calendar/?eventid=${encodeURIComponent(calendarEvent.id)}`;

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);
    try {
      iframe.src = outlookDesktopUrl;
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
          if (document.hasFocus()) {
            window.open(outlookWebUrl, "_blank", "noopener,noreferrer");
          }
        }
      }, 2000);
    } catch {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
      window.open(outlookWebUrl, "_blank", "noopener,noreferrer");
    }
  };

  const timeStr = `${startTime.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })} - ${endTime.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}`;
  const dateStr = startTime.toLocaleDateString("sv-SE", { weekday: "short", month: "short", day: "numeric" });

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(statusColor, 0.3),
        borderLeft: `3px solid ${statusColor}`,
        overflow: "hidden",
      }}
    >
      {/* Compact header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { bgcolor: alpha(statusColor, 0.03) },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <VideocamIcon sx={{ fontSize: 18, color: statusColor }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {calendarEvent.subject}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dateStr} • {timeStr}
          </Typography>
        </Box>

        {isOngoing && (
          <Chip
            label="LIVE"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.65rem",
              bgcolor: "error.main",
              color: "white",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.6 },
              },
            }}
          />
        )}

        {attendees.length > 0 && (
          <AvatarGroup
            max={3}
            sx={{
              "& .MuiAvatar-root": { width: 22, height: 22, fontSize: "0.6rem", border: "1px solid white" },
            }}
          >
            {attendees.map((a, i) => (
              <Tooltip key={i} title={a.emailAddress.name}>
                <Avatar sx={{ bgcolor: getAvatarColor(a.emailAddress.name) }}>
                  {getInitials(a.emailAddress.name)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        )}

        {/* Action buttons inline */}
        {isOnlineMeeting && !isPast && (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => { e.stopPropagation(); handleJoinMeeting(); }}
            startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: "none",
              fontSize: "0.7rem",
              py: 0.25,
              px: 1.5,
              borderRadius: 1.5,
              bgcolor: statusColor,
              minWidth: "auto",
            }}
          >
            {isOngoing ? "Anslut" : "Teams"}
          </Button>
        )}

        <Tooltip title="Öppna i Outlook">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleOpenOutlook(); }}
            sx={{ color: "text.secondary", "&:hover": { color: statusColor } }}
          >
            <CalendarIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {expanded ? <ExpandLessIcon sx={{ fontSize: 18, color: "text.disabled" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
      </Box>

      {/* Expandable details */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          {/* Organizer */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Organisatör: {calendarEvent.organizer.emailAddress.name}
            </Typography>
          </Box>

          {/* Attendees list */}
          {attendees.length > 0 && (
            <Box sx={{ pl: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Deltagare ({attendees.length})
              </Typography>
              {attendees.map((attendee, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.5,
                    px: 1,
                    mb: 0.25,
                    borderRadius: 1,
                    bgcolor: "grey.50",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 20,
                      height: 20,
                      fontSize: "0.6rem",
                      bgcolor: getAvatarColor(attendee.emailAddress.name),
                    }}
                  >
                    {getInitials(attendee.emailAddress.name)}
                  </Avatar>
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {attendee.emailAddress.name}
                  </Typography>
                  {attendee.status?.response && (
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          attendee.status.response === "accepted" ? "success.main"
                          : attendee.status.response === "declined" ? "error.main"
                          : "text.disabled",
                      }}
                    >
                      {attendee.status.response === "accepted" ? "✓"
                        : attendee.status.response === "declined" ? "✗"
                        : attendee.status.response === "tentativelyAccepted" ? "?"
                        : "·"}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default MeetingCard;
